import { Button, ErrorState, Input, PageHeader, Select, SkeletonRows, Tabs } from '@pm/ui';
import { format, startOfMonth } from 'date-fns';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { fmtDateTime, PRIORITY_LABEL, STATUS_LABEL, str } from '@/lib/format';
import { downloadFile, qs, useContexts, useReport } from '@/lib/queries';
import { useToast } from '@/lib/toast';

type Kind = 'tasks' | 'users' | 'teams' | 'clients' | 'weekly-deliveries';
const KINDS: { value: Kind; label: string }[] = [
  { value: 'tasks', label: 'Demandas' },
  { value: 'users', label: 'Por usuário' },
  { value: 'teams', label: 'Por time' },
  { value: 'clients', label: 'Por cliente' },
  { value: 'weekly-deliveries', label: 'Entregas por semana' },
];
const DATE_KEYS = new Set(['created_at', 'due_at', 'completed_at']);

/** API §19 — relatórios tabulares com exportação CSV (reports.export). */
export function ReportsPage() {
  const toast = useToast();
  const { can } = useAuth();
  const [kind, setKind] = useState<Kind>('tasks');
  const [from, setFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [contextId, setContextId] = useState('');
  const [exporting, setExporting] = useState(false);
  const { data: contexts = [] } = useContexts({ status: 'ACTIVE' });
  const query = {
    from: new Date(`${from}T00:00:00`).toISOString(),
    to: new Date(`${to}T23:59:59`).toISOString(),
    context_id: contextId || undefined,
  };
  const { data, isLoading, isError, error, refetch } = useReport(kind, query);

  async function exportCsv() {
    setExporting(true);
    try {
      await downloadFile(
        `/reports/${kind}${qs({ ...query, format: 'csv' })}`,
        `relatorio-${kind}-${from}-${to}.csv`,
      );
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  }

  const cell = (key: string, value: unknown) => {
    if (value === null || value === undefined) return <span className="text-text-3">—</span>;
    if (DATE_KEYS.has(key))
      return <span className="font-mono text-xs">{fmtDateTime(str(value))}</span>;
    if (key === 'status') return STATUS_LABEL[str(value)] ?? str(value);
    if (key === 'priority') return PRIORITY_LABEL[str(value)] ?? str(value);
    if (typeof value === 'number') return <span className="font-mono">{value}</span>;
    return str(value);
  };

  return (
    <div className="flex flex-col gap-gap-lg">
      <PageHeader
        title="Relatórios"
        subtitle="Tabelas exportáveis. Tarefas pessoais não entram."
        actions={
          <>
            <Input
              type="date"
              mono
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-control-sm w-[140px] text-sm"
              aria-label="De"
            />
            <span className="text-text-3">→</span>
            <Input
              type="date"
              mono
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-control-sm w-[140px] text-sm"
              aria-label="Até"
            />
            <Select
              value={contextId}
              onChange={(e) => setContextId(e.target.value)}
              className="h-control-sm w-[180px] text-sm"
              aria-label="Contexto"
            >
              <option value="">Todos os contextos</option>
              {contexts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            {can('reports.export') ? (
              <Button variant="primary" loading={exporting} onClick={() => void exportCsv()}>
                <Download /> Exportar CSV
              </Button>
            ) : null}
          </>
        }
      />
      <Tabs value={kind} onChange={setKind} tabs={KINDS} />
      <div className="overflow-x-auto rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : !data || data.rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-text-3">Sem linhas para o período.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                {data.columns.map((c) => (
                  <th key={c.key} className="h-8 px-3 text-left font-medium whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr
                  key={i}
                  className="h-row border-b border-border text-sm last:border-b-0 hover:bg-surface-2"
                >
                  {data.columns.map((c) => (
                    <td key={c.key} className="max-w-[360px] truncate px-3">
                      {cell(c.key, r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data ? <p className="font-mono text-xs text-text-3">{data.rows.length} linhas</p> : null}
    </div>
  );
}
